import dayjs from "dayjs";
import {
	Car,
	Crosshair,
	FileText,
	Fish,
	Globe,
	GraduationCap,
	HeartPulse,
	House,
	IdCard,
	type LucideIcon,
	Plane,
	Ship,
	Target,
	Ticket,
	Wrench,
} from "lucide-react";
import {
	ClientIdentificationDocumentTypeEnum,
	type IdentificationDocumentListItemResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";

export type DocumentValidityStatus = "valid" | "expiring" | "expired" | "none";

const EXPIRING_SOON_DAYS = 90;

const DOCUMENT_TYPE_ICON: Record<
	ClientIdentificationDocumentTypeEnum,
	LucideIcon
> = {
	[ClientIdentificationDocumentTypeEnum.IdentityCard]: IdCard,
	[ClientIdentificationDocumentTypeEnum.Passport]: Globe,
	[ClientIdentificationDocumentTypeEnum.DrivingLicense]: Car,
	[ClientIdentificationDocumentTypeEnum.ResidencePermit]: House,
	[ClientIdentificationDocumentTypeEnum.ShipLicense]: Ship,
	[ClientIdentificationDocumentTypeEnum.PilotLicense]: Plane,
	[ClientIdentificationDocumentTypeEnum.WeaponLicense]: Crosshair,
	[ClientIdentificationDocumentTypeEnum.ISIC]: GraduationCap,
	[ClientIdentificationDocumentTypeEnum.TravelLicense]: Ticket,
	[ClientIdentificationDocumentTypeEnum.STK]: Wrench,
	[ClientIdentificationDocumentTypeEnum.InsuranceCard]: HeartPulse,
	[ClientIdentificationDocumentTypeEnum.FishingLicense]: Fish,
	[ClientIdentificationDocumentTypeEnum.HuntingLicense]: Target,
	[ClientIdentificationDocumentTypeEnum.Other]: FileText,
};

export const getDocumentIcon = (
	type?: ClientIdentificationDocumentTypeEnum | null,
): LucideIcon => (type ? DOCUMENT_TYPE_ICON[type] : FileText);

export const getPaperworkDocumentDisplayName = (
	document: IdentificationDocumentListItemResponseDTO,
	typeName: string,
): string => document.name || typeName;

export const getDocumentValidity = (
	validToDate?: string | null,
): { status: DocumentValidityStatus; formatted?: string } => {
	if (!validToDate) return { status: "none" };

	const expiry = dayjs(validToDate);
	if (!expiry.isValid()) return { status: "none" };

	const daysUntil = expiry.diff(dayjs(), "day");
	const status: DocumentValidityStatus =
		daysUntil < 0
			? "expired"
			: daysUntil <= EXPIRING_SOON_DAYS
				? "expiring"
				: "valid";

	return { status, formatted: expiry.format("MM/YYYY") };
};

// Identity card and passport are pinned to the top of the list; the rest sorts by display name.
const PINNED_TYPE_RANK: Partial<
	Record<ClientIdentificationDocumentTypeEnum, number>
> = {
	[ClientIdentificationDocumentTypeEnum.IdentityCard]: 0,
	[ClientIdentificationDocumentTypeEnum.Passport]: 1,
};

const UNPINNED_RANK = 2;

const compareValidTo = (a?: string | null, b?: string | null): number => {
	if (a && b) return new Date(a).valueOf() - new Date(b).valueOf();
	if (a) return -1;
	if (b) return 1;
	return 0;
};

export const sortIdentificationDocuments = (
	documents: IdentificationDocumentListItemResponseDTO[],
	getDisplayName: (
		document: IdentificationDocumentListItemResponseDTO,
	) => string,
): IdentificationDocumentListItemResponseDTO[] =>
	documents.slice().sort((a, b) => {
		const rankA = PINNED_TYPE_RANK[a.documentType] ?? UNPINNED_RANK;
		const rankB = PINNED_TYPE_RANK[b.documentType] ?? UNPINNED_RANK;
		if (rankA !== rankB) return rankA - rankB;

		if (rankA === UNPINNED_RANK) {
			const nameCompare = getDisplayName(a).localeCompare(
				getDisplayName(b),
				"cs",
			);
			if (nameCompare !== 0) return nameCompare;
		}

		return compareValidTo(a.validTo, b.validTo);
	});
